import { AvailableTemplate, DataCell, ExcelExportDto, TableColumn } from '@ecosystem/export-api';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import { CreateImagePowerPointParams, ExcelExportParams, ExcelRowArgs, ExportChartArgs } from '../Model';
import { saveFile } from '../SaveFile';
import { ExportContext } from '@utils/pageConfig/Types';
import {
  getStackedChartExportColumn,
  getStackedChartExportRow,
  getStackedChartSummaryRow,
} from './ExportStackedChartExcel';
import { getContext, getFilename } from '../ExportUtils';
import { createImage, createPowerPoint } from '../ExportChart';
import { PerformanceAnalysisView } from '@components/performance-analysis-card/Types';
import { constructCurrencyLabel, formatLabelIntoRevenuePriceTitle } from '@utils/currencyUtils/CurrencyUtils';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';

const createExcel = async ({
  filename,
  exportFn,
  appliedCountry,
  appliedPg,
  data,
  factRevenue,
  excelRowArgs,
  title,
  volume = DEFAULT_VOLUME,
  loadedCountries,
  loadedProductGroups,
  pagesConfig,
  pageName,
}: ExcelExportParams) => {
  const dataColumns: TableColumn[] = getStackedChartExportColumn(data);
  const dataRows: DataCell[][] = getStackedChartExportRow(data, excelRowArgs.selectedKpi.value, factRevenue, volume);
  const summaryRows: DataCell[][] = getStackedChartSummaryRow(data);
  const columnWidths = [
    { columnNumber: 1, width: factRevenue.currencyLabel ? 27 : 25 }, // the width needs to be slightly bigger to since adding currency label = adding more words
    { columnNumber: 2, width: 30 },
    { columnNumber: 3, width: 15 },
    { columnNumber: 4, width: 15 },
    { columnNumber: 5, width: 15 },
    { columnNumber: 6, width: 15 },
    { columnNumber: 7, width: 12 },
    { columnNumber: 8, width: 12 },
    { columnNumber: 9, width: 12.7 },
    { columnNumber: 10, width: 12.7 },
    { columnNumber: 11, width: 15 },
    { columnNumber: 12, width: 15 },
  ];

  const exportContext: ExportContext = {
    appliedCountry,
    appliedPg,
    timePeriod: excelRowArgs.timePeriod,
    comparisonTimePeriod: excelRowArgs.comparisonTimePeriod,
    periodType: excelRowArgs.periodType,
    showPerformanceWithin: excelRowArgs.showPerformanceWithin,
    appliedFiltersText: excelRowArgs.appliedFiltersText,
    loadedCountries,
    loadedProductGroups,
    pagesConfig,
    pageName,
  };

  const exportData: ExcelExportDto = {
    template: AvailableTemplate.Excel,
    name: filename,
    worksheets: [
      {
        worksheetName: title,
        context: getContext(exportContext),
        data: {
          dataRows,
          summaryRows: summaryRows,
          dataColumns,
          columnWidths,
          currencySymbol: factRevenue.currencySymbol,
        },
      },
    ],
  };

  const exportFile = await exportFn(exportData);
  saveFile(exportFile);
};

export async function exportStackedChart(args: ExportChartArgs): Promise<void> {
  const {
    exportFn,
    exportFormats,
    invokeExport,
    title,
    appliedCountry,
    appliedPg,
    data,
    timePeriod,
    comparisonTimePeriod,
    factRevenue,
    periodType,
    showPerformanceWithin,
    appliedFiltersText,
    selectedKpi,
    segmentBy,
    volume = DEFAULT_VOLUME,
    loadedCountries,
    loadedProductGroups,
    pagesConfig,
    pageName,
    exportImage,
  } = args;
  const rowArgs: ExcelRowArgs = {
    timePeriod,
    comparisonTimePeriod,
    periodType,
    showPerformanceWithin,
    appliedFiltersText,
    selectedKpi,
  };

  const filename = getFilename({
    title,
    appliedCountry,
    appliedPg,
    timeframe: timePeriod,
    comparisonTimePeriod,
    exportFormat: ExportFormat.Xlsx,
    selectedKpi: selectedKpi,
    viewType: PerformanceAnalysisView.StackedBarChart,
    pageName: pageName,
  });
  const chartTitle = `${formatLabelIntoRevenuePriceTitle(
    selectedKpi.label,
    constructCurrencyLabel(factRevenue),
  )} by ${segmentBy}`;

  if (exportFormats.includes(ExportFormat.Xlsx)) {
    const excelExportParams: ExcelExportParams = {
      filename,
      exportFn,
      appliedCountry,
      appliedPg,
      data,
      factRevenue,
      excelRowArgs: rowArgs,
      title,
      volume,
      loadedCountries,
      loadedProductGroups,
      pagesConfig,
      pageName,
    };
    await createExcel(excelExportParams);
  }
  if (exportFormats.includes(ExportFormat.Png)) {
    const methodParam: CreateImagePowerPointParams = {
      filename,
      exportFn,
      appliedCountry,
      appliedPg,
      timePeriod,
      comparisonTimePeriod,
      periodType,
      showPerformanceWithin,
      appliedFiltersText,
      invokeExport,
      selectedKpi,
      segmentBy,
      title,
      subTitle: chartTitle,
      loadedCountries,
      loadedProductGroups,
      pagesConfig,
      pageName,
      exportImage,
    };
    await createImage(methodParam, `${title} \n${chartTitle}`);
  }
  if (exportFormats.includes(ExportFormat.Pptx)) {
    const methodParam: CreateImagePowerPointParams = {
      filename,
      exportFn,
      appliedCountry,
      appliedPg,
      timePeriod,
      comparisonTimePeriod,
      periodType,
      showPerformanceWithin,
      appliedFiltersText,
      invokeExport,
      selectedKpi,
      segmentBy,
      title,
      subTitle: chartTitle,
      loadedCountries,
      loadedProductGroups,
      pagesConfig,
      pageName,
      exportImage,
    };

    await createPowerPoint(methodParam);
  }
}
